export const frameworkStages = [
  {
    title: 'Case Information',
    text: 'Gather the available information, relevant history, and existing records before beginning your review.',
  },
  {
    title: 'Periodontal Findings',
    text: 'Organize the available clinical findings and distinguish documented information from questions that remain unanswered.',
  },
  {
    title: 'Classification & Reasoning',
    text: 'Study the relevant periodontal classification concepts and structure the reasoning behind your interpretation.',
  },
  {
    title: 'Complexity & Risk Review',
    text: 'Identify factors that may require additional assessment, verification, or professional consideration.',
  },
  {
    title: 'Treatment Objectives',
    text: 'Organize educational considerations and document the objectives that need to be addressed in the case.',
  },
  {
    title: 'Documentation & Follow-Up',
    text: 'Structure your case summary, record unresolved questions, and organize information for subsequent review.',
  },
];

export const problemPoints = [
  'Switching between different references to review related periodontal concepts.',
  'Trying to remember which information is still missing.',
  'Studying classification criteria without having a clear picture of how the concepts connect.',
  'Struggling to organize periodontal findings into a coherent case discussion.',
  'Spending too much time preparing educational case presentations.',
  'Looking for a consistent visual framework for your initial case review.',
  'Recreating study materials and reference sheets you’ve already made before.',
];

export const beforeItems = [
  'You review several disconnected resources.',
  'You struggle to see how different periodontal concepts relate.',
  'You spend time organizing your notes before discussing a case.',
  'You have no consistent visual framework for your initial case review.',
  'You find it difficult to identify and document missing information systematically.',
  'You repeatedly recreate study materials and reference sheets.',
  'You may find it difficult to explain how the different parts of your analysis connect.',
];

export const afterItems = [
  'You have a master map connecting the major areas of Periodontology.',
  'You can organize your initial case review using a repeatable sequence.',
  'You can document findings, questions, and information gaps more systematically.',
  'You have dedicated visual references for different periodontal topics.',
  'You can use structured worksheets to prepare case discussions.',
  'You have reusable educational references instead of repeatedly rebuilding your own notes.',
  'You have a clearer structure for presenting your reasoning and documenting unresolved questions.',
];

export const situations = [
  {
    title: 'You Have the Knowledge, but You Want a Clearer Structure',
    text: 'You understand periodontal concepts, but connecting them into an organized case review can still take time.',
  },
  {
    title: 'You Spend Too Much Time Reviewing Notes',
    text: 'Before discussing a case, you find yourself opening multiple documents and references to organize the information you need.',
  },
  {
    title: 'You Want a Better Way to Study Periodontal Classification',
    text: 'You want to understand how classification concepts relate rather than memorizing isolated definitions without a clear structure.',
  },
  {
    title: 'You Want to Organize Your Clinical Questions',
    text: 'You need a consistent way to distinguish the information available from the questions that still need to be answered.',
  },
  {
    title: 'You Prepare Periodontal Cases for Discussion',
    text: 'You want a more organized way to present findings, document your reasoning, and explain which questions require further evaluation.',
  },
  {
    title: 'You Want a Practical Reference You Can Reuse',
    text: 'You prefer having structured maps, worksheets, and checklists available instead of repeatedly recreating your own study materials.',
  },
];

export const toolkit = [
  {
    title: 'The Periodontal Master Map',
    text: 'A one-page overview connecting the major subject areas covered by the system.',
  },
  {
    title: 'Periodontal Assessment Maps',
    text: 'Visual references for organizing examination information, classification concepts, and case-review questions.',
  },
  {
    title: 'Case Analysis Worksheets',
    text: 'Structured worksheets for documenting available information, findings, questions, and educational conclusions.',
  },
  {
    title: 'Risk & Complexity Review Filters',
    text: 'Educational prompts for recording factors that may require additional assessment.',
  },
  {
    title: 'Periodontal Case Review Checklist',
    text: 'A reusable checklist for organizing case information before a clinical discussion or presentation.',
  },
  {
    title: 'Printable and Digital Reference Sheets',
    text: 'Use the materials in your preferred study environment, whether printed or on a tablet.',
  },
];

export const lookFor = [
  {
    title: 'Clear Organization',
    text: 'Can you understand how the major periodontal subject areas relate to one another?',
  },
  {
    title: 'Practical Educational Resources',
    text: 'Do the maps and worksheets help you structure your study and case discussions?',
  },
  {
    title: 'Reusability',
    text: 'Can you return to the materials when reviewing a different topic or preparing an educational case?',
  },
];

export const guaranteeItems = [
  'Explore all six volumes.',
  'Review the Periodontal Master Map.',
  'Open the assessment and classification maps.',
  'Work through the educational cases.',
  'Try the case-review worksheets.',
  'Review the checklists and study resources.',
];
